import T from "@/components/forms/TextField.module.css";

import { useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { WFakeForm } from "@/components/forms/WHookForm.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { omiseCreateSource, omiseLoader, usePreloadOmise } from "@/modules/billing/omise/omiseLoader.ts";
import { checkForOmisePersistentData } from "@/modules/billing/omise/parseOmisePersistentData.ts";
import { PaymentResult, type PaymentParams, type PaymentProps } from "@/modules/billing/payment/PaymentParams.ts";
import {
    finalTopupAmountRequest,
    getRedirectUrlFromData,
    linkSendData,
    pollForSuccess,
    redirectToGatewayAndWaitForResponse,
} from "@/modules/billing/payment/paymentUtils.ts";
import { DescriptionLayout, ErrorText } from "@/modules/billing/payment_forms/components.tsx";
import {
    t_ConnectingPaymentMethod,
    t_LoadingPaymentProvider,
    t_StartingPayment,
    t_WaitingForPaymentConfirmation,
} from "@/modules/billing/progressLabels.tsx";
import { QrCodeDisplay, type QrCode } from "@/modules/billing/QrCodeDisplay.tsx";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import { useLingui } from "@lingui/react/macro";
import type {
    LinkParamsOmise,
    MethodConfig,
    OmiseConfig,
    OmiseQrSendDataResponse,
    OmiseRedirectSendDataResponse,
    PersistentDataResponse,
} from "@warrenio/api-spec/spec.oats.gen";
import { unreachableSwitchCase } from "@warrenio/utils/unreachable";
import type { OmiseCreateSourceParameters } from "omise-js-typed/dist/lib/omise";
import type { Currency, Payment } from "omise-js-typed/dist/lib/utils";
import { useImperativeHandle, useRef, useState, type Ref } from "react";
import { Input, TextField } from "react-aria-components";
import { useForm } from "react-hook-form";

export default function OmisePaymentForm({
    actionsRef,
    isSubmitting,
    method: { key },
    config: { public_key },
}: PaymentProps & { method: MethodConfig; config: OmiseConfig }) {
    //#region Hooks
    const [qrCode, setQrCode] = useState<QrCode | null>(null);

    usePreloadOmise();
    const apiClient = useApiClient();
    const signal = useUnmountSignal();
    const getExtraRef = useRef<GetExtraSourceFields>(null);

    useImperativeHandle(actionsRef, () => ({
        needsPopUp(_params) {
            return key !== "promptpay";
        },
        async makePayment(params: PaymentParams) {
            const { currency, progress, setupAmount, account, popup } = params;

            // Query the form component for extra fields
            const extraFields = await getExtraRef.current?.();
            if (extraFields === false) {
                return PaymentResult.VALIDATION_FAILED;
            }

            const billing_account_id = account.id;

            progress(t_LoadingPaymentProvider);
            const omise = await omiseLoader();
            omise.setPublicKey(public_key);

            progress(t_ConnectingPaymentMethod);
            const chargeAmount =
                params.target.type === "invoice"
                    ? setupAmount
                    : (
                          await finalTopupAmountRequest(apiClient, {
                              billing_account_id,
                              processor: "omise",
                              amount: setupAmount,
                          })
                      ).final_amount;
            const sourceResult = await omiseCreateSource(omise, key as Payment, {
                amount: Math.round(chargeAmount * 100),
                currency: currency as Currency,
                ...extraFields,
            });

            progress(t_StartingPayment);
            const sendResult = await linkSendData<
                OmiseQrSendDataResponse | OmiseRedirectSendDataResponse,
                LinkParamsOmise
            >(apiClient, params, "omise", {
                paymentMethod: key,
                source_id: sourceResult.id,
                currency,
            });
            const { data } = sendResult;

            if ("qrcode_url" in data) {
                // "promptpay" method
                progress(t_WaitingForPaymentConfirmation);

                const { qrcode_url, persistent_data_ref } = data satisfies OmiseQrSendDataResponse;
                setQrCode({ qrcode_url });

                // 15 min polling to allow scanning QR code
                return await pollForSuccess({
                    signal,
                    popup,
                    pollInterval: 5000,
                    maxPollCount: 180,
                    check: async () =>
                        await checkForOmisePersistentData({ apiClient, account, persistent_data_ref, signal }),
                });
            } else if ("redirect_url" in data) {
                const { persistent_data_ref } = data satisfies OmiseRedirectSendDataResponse;

                return await redirectToGatewayAndWaitForResponse({
                    popup,
                    progress,
                    redirectUrl: getRedirectUrlFromData(data),
                    signal,
                    check: async () =>
                        await checkForOmisePersistentData({ apiClient, account, persistent_data_ref, signal }),
                });
                // TODO: Handle return: "?omise_return=1&billing_account_id=131623&reference=ddb9a1fe-514a-4203-9445-d756d0ad9cc5"
            } else {
                unreachableSwitchCase(data);
            }
        },
    }));
    //#endregion

    if (qrCode) {
        return <QrCodeDisplay qrCode={qrCode} />;
    }

    if (key === "truemoney") {
        return <TrueMoneyForm isSubmitting={isSubmitting} getExtraRef={getExtraRef} />;
    } else {
        return null;
    }
}

type ExtraSourceParams = Partial<OmiseCreateSourceParameters>;

/**
 * Function to get extra fields for {@link Omise.createSource}.
 *
 * @returns Extra source parameters, or `false` if validation failed
 */
type GetExtraSourceFields = () => Promise<ExtraSourceParams | false>;

interface FormProps {
    isSubmitting: boolean;
    getExtraRef: Ref<GetExtraSourceFields>;
}

function TrueMoneyForm({ getExtraRef, isSubmitting }: FormProps) {
    const { t } = useLingui();
    interface TrueMoneyInputs {
        phone_number: ExtraSourceParams["phone_number"] & string;
    }

    //#region Hooks
    const form = useForm<TrueMoneyInputs>({
        disabled: isSubmitting,
        defaultValues: {
            phone_number: "",
        },
    });

    useImperativeHandle(getExtraRef, () => async () => {
        const valid = await form.trigger(undefined, { shouldFocus: true });
        if (!valid) {
            console.debug("Form validation failed");
            return false;
        }

        return form.getValues();
    });

    const { ref, props } = useAriaField("phone_number", form, { isRequired: true });
    //#endregion

    return (
        <WFakeForm form={form}>
            <TextField className={T.TextField} {...props}>
                <DescriptionLayout
                    label={t`Phone number`}
                    description={t`10 digit TrueMoney mobile number starting with 0`}
                >
                    <Input className={T.Input} ref={ref} autoFocus={true} />
                    <ErrorText text={props.errorMessage} />
                </DescriptionLayout>
            </TextField>
        </WFakeForm>
    );
}

//#region Example responses from `/payment/persistent_data`
// cSpell:disable
const _examplePendingResponse: PersistentDataResponse = {
    billing_account_id: 131623,
    created: 1719849261,
    data: "{}",
    id: 561,
    identifier: "1085f086-0091-441f-8768-32be745148d4",
    processor_method: "omise::promptpay",
    status: "pending",
};

const _exampleSuccessResponse: PersistentDataResponse = {
    billing_account_id: 131623,
    created: 1719849261,
    data: JSON.stringify({
        object: "charge",
        id: "chrg_test_609qsjru8nzr3zdvmjk",
        location: "/charges/chrg_test_609qsjru8nzr3zdvmjk",
        amount: 2000,
        authorization_type: null,
        authorized_amount: 2000,
        captured_amount: 2000,
        acquirer_reference_number: null,
        net: 1965,
        fee: 33,
        fee_vat: 2,
        interest: 0,
        interest_vat: 0,
        funding_amount: 2000,
        refunded_amount: 0,
        transaction_fees: { fee_flat: "0.0", fee_rate: "1.65", vat_rate: "7.0" },
        platform_fee: { fixed: null, amount: null, percentage: null },
        currency: "THB",
        funding_currency: "THB",
        ip: null,
        refunds: {
            object: "list",
            data: [],
            limit: 20,
            offset: 0,
            total: 0,
            location: "/charges/chrg_test_609qsjru8nzr3zdvmjk/refunds",
            order: "chronological",
            from: "1970-01-01T00:00:00Z",
            to: "2024-07-01T15:56:25Z",
        },
        link: null,
        description: "Top up for 131623",
        metadata: {
            amount_with_vat: 20.0,
            amount_without_vat: 20,
            billing_account_id: 131623,
            persistent_data_ref: "1085f086-0091-441f-8768-32be745148d4",
        },
        card: null,
        source: {
            object: "source",
            id: "src_test_609qsjj81wpjpt21mht",
            livemode: false,
            location: "/sources/src_test_609qsjj81wpjpt21mht",
            amount: 2000,
            barcode: null,
            bank: null,
            created_at: "2024-07-01T15:54:21Z",
            currency: "THB",
            email: null,
            flow: "offline",
            installment_term: null,
            ip: "90.190.251.66",
            absorption_type: null,
            name: null,
            mobile_number: null,
            phone_number: null,
            platform_type: null,
            scannable_code: {
                object: "barcode",
                type: "qr",
                image: {
                    object: "document",
                    livemode: false,
                    id: "docu_test_609qsjufiuq8ywbnzdk",
                    deleted: false,
                    filename: "qrcode.svg",
                    location: "/charges/chrg_test_609qsjru8nzr3zdvmjk/documents/docu_test_609qsjufiuq8ywbnzdk",
                    kind: "qr",
                    download_uri:
                        "https://api.omise.co/charges/chrg_test_609qsjru8nzr3zdvmjk/documents/docu_test_609qsjufiuq8ywbnzdk/downloads/00B9291A84591270",
                    created_at: "2024-07-01T15:54:22Z",
                },
            },
            billing: null,
            shipping: null,
            items: [],
            references: null,
            provider_references: { reference_number_1: "pay2_test_609qsjrwot22q69lttd", reference_number_2: null },
            store_id: null,
            store_name: null,
            terminal_id: null,
            type: "promptpay",
            zero_interest_installments: null,
            charge_status: "successful",
            receipt_amount: null,
            discounts: [],
            promotion_code: null,
        },
        schedule: null,
        linked_account: null,
        customer: null,
        dispute: null,
        transaction: "trxn_test_609qt9qih5t0yfii0xx",
        failure_code: null,
        failure_message: null,
        status: "successful",
        authorize_uri: "https://pay.omise.co/payments/pay2_test_609qsjrwot22q69lttd/authorize",
        return_uri:
            "https://test.pilw.io/user/shared-test%2Bdemo@warren.io/lab?redirects=1?omise_return=1&billing_account_id=131623&reference=1085f086-0091-441f-8768-32be745148d4",
        created_at: "2024-07-01T15:54:22Z",
        paid_at: "2024-07-01T15:56:25Z",
        authorized_at: "2024-07-01T15:56:25Z",
        expires_at: "2024-07-02T15:54:22Z",
        expired_at: null,
        reversed_at: null,
        zero_interest_installments: false,
        branch: null,
        terminal: null,
        device: null,
        authorized: true,
        capturable: false,
        capture: true,
        disputable: false,
        livemode: false,
        refundable: false,
        partially_refundable: false,
        reversed: false,
        reversible: false,
        voided: false,
        paid: true,
        expired: false,
        can_perform_void: false,
        approval_code: null,
    }),
    id: 561,
    identifier: "1085f086-0091-441f-8768-32be745148d4",
    processor_method: "omise::promptpay",
    status: "success",
};
//#endregion
